import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Package,
  TrendingUp,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';
import { RevenueOrderTrendCard } from './dashboard/RevenueOrderTrendCard';
import { CreditMemoAnalyticsCard } from './dashboard/CreditMemoAnalyticsCard';
import { AtRiskClientsCard } from './dashboard/AtRiskClientsCard';

const statusColors = {
  submitted: 'bg-yellow-100 text-yellow-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_production: 'bg-purple-100 text-purple-800',
  ready_for_delivery: 'bg-indigo-100 text-indigo-800',
  out_for_delivery: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  failed_delivery: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kpisLoading, setKpisLoading] = useState(true);
  const KPI_MONTHS = 6;

  useEffect(() => {
    loadStats();
    loadKpis();
  }, []);

  const loadStats = async () => {
    try {
      const data = await adminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadKpis = async () => {
    try {
      const data = await adminService.getDashboardKPIs(KPI_MONTHS);
      setKpis(data);
    } catch (error) {
      console.error('Error loading KPIs:', error);
      toast.error('Failed to load reporting KPIs');
    } finally {
      setKpisLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-dashboard-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Today's Orders</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.today.orders_count}</p>
                <p className="text-sm text-gray-500 mt-1">${stats.today.orders_value.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Orders</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.pending_orders}</p>
                <p className="text-sm text-amber-600 mt-1">Requires attention</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">This Week</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">${stats.week_revenue.toFixed(0)}</p>
                <p className="text-sm text-green-600 mt-1">Revenue</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Clients</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_clients}</p>
                <p className="text-sm text-gray-500 mt-1">{stats.active_products} products</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <Card data-testid="recent-orders-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <Link to="/admin/orders">
              <Button variant="ghost" size="sm" className="text-amber-600">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_orders.slice(0, 5).map((order) => (
                <div 
                  key={order.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{order.order_number}</p>
                      <Badge className={statusColors[order.status]}>
                        {order.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{order.client_name}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-gray-900">${order.total_amount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      {(() => { const s = order.delivery_date; const d = s ? new Date(typeof s === 'string' && s.length === 10 ? s + 'T12:00:00' : s) : null; return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'; })()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Top Products (30 Days)</CardTitle>
            <Link to="/admin/products">
              <Button variant="ghost" size="sm" className="text-amber-600">
                Manage <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats.top_products.length > 0 ? (
              <div className="space-y-3">
                {stats.top_products.map((product, index) => (
                  <div 
                    key={product.product_id} 
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.product_name}</p>
                      <p className="text-sm text-gray-500">{product.quantity} packs sold</p>
                    </div>
                    <p className="font-semibold text-green-600">${product.revenue.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No sales data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reporting / KPI block */}
      <RevenueOrderTrendCard
        data={kpis?.revenue_trend || []}
        loading={kpisLoading}
        months={KPI_MONTHS}
      />

      <CreditMemoAnalyticsCard
        data={kpis?.credit_memos}
        loading={kpisLoading}
        months={KPI_MONTHS}
      />

      <AtRiskClientsCard />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link to="/admin/orders?status=submitted">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                <ShoppingCart className="h-5 w-5 text-amber-500" />
                <span>New Orders</span>
              </Button>
            </Link>
            <Link to="/admin/products">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                <Package className="h-5 w-5 text-amber-500" />
                <span>Manage Products</span>
              </Button>
            </Link>
            <Link to="/admin/clients">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                <Users className="h-5 w-5 text-amber-500" />
                <span>View Clients</span>
              </Button>
            </Link>
            <Link to="/admin/messages">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                <TrendingUp className="h-5 w-5 text-amber-500" />
                <span>Send Message</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
